import type { ReactNode } from "react";
import type { PopoverProps } from "react-aria-components";
import {
  Dialog,
  DialogTrigger,
  OverlayArrow,
  Popover as ReactAriaPopover,
} from "react-aria-components";
import { cn } from "../../utilities/tailwind";

export type PopoverProperties = {
  /**
   * Class name to apply to the `Popover`
   */
  className?: string | undefined;

  /**
   * Class name to apply to the `Popover` dialog
   */
  dialogClassName?: string | undefined;

  /**
   * Content that will be displayed when the popover is open
   */
  children: ReactNode;

  /**
   * Show or hide the popover overlay arrow
   */
  showOverlayArrow?: boolean | undefined;

  /**
   * Our design system Button, or the React Aria Button should be used as the trigger element
   */
  triggerButton: ReactNode;
} & Omit<
  PopoverProps,
  | "UNSTABLE_portalContainer"
  | "children"
  | "className"
  | "containerPadding"
  | "style"
  | "trigger"
>;
export const Popover = ({
  children,
  className = "",
  dialogClassName = "",
  offset = 12,
  showOverlayArrow = true,
  triggerButton,
  ...properties
}: PopoverProperties) => {
  const twClassName = cn(
    "rounded-md border border-solid border-outline bg-surface-container p-4 md:shadow-jb-2",
    className,
  );
  const twDialogClassName = cn("min-w-28 outline-none", dialogClassName);

  return (
    <DialogTrigger>
      {triggerButton}

      <ReactAriaPopover
        className={twClassName}
        containerPadding={0}
        offset={offset}
        {...properties}
      >
        {showOverlayArrow && (
          <OverlayArrow className="fill-surface-container stroke-outline data-[placement=bottom]:rotate-180! data-[placement=left]:-rotate-90! data-[placement=right]:rotate-90!">
            <svg height={12} viewBox="0 0 12 12" width={12}>
              <path d="M0 0 L6 6 L12 0" />
            </svg>
          </OverlayArrow>
        )}

        <Dialog className={twDialogClassName}>{children}</Dialog>
      </ReactAriaPopover>
    </DialogTrigger>
  );
};

Popover.displayName = "Popover";
